import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Card, Col, Form, Input, Button, CardBody, Label, Badge, Alert, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import * as actions from '../../store/actions';
import { toast } from "react-toastify";

export default function AboutUsSettings({ about }) {
    const { about_us, id } = about;
    const [aboutUsText, setAboutUsText] = useState("");
    const [editedText, setEditedText] = useState("");
    const [activeTab, setActiveTab] = useState('1');
    const [hasChanges, setHasChanges] = useState(false);
    const dispatch = useDispatch();

    const MAX_LENGTH = 1000;
    const charCount = editedText?.length || 0;
    const charRemaining = MAX_LENGTH - charCount;

    useEffect(() => {
        setAboutUsText(about_us || "");
        setEditedText(about_us || "");
    }, [about_us]);

    useEffect(() => {
        setHasChanges(editedText !== aboutUsText);
    }, [editedText, aboutUsText]);

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!editedText.trim()) {
            toast.error("لا يمكن حفظ نص فارغ");
            return;
        }

        if (editedText.length > MAX_LENGTH) {
            toast.error(`النص طويل جداً. الحد الأقصى ${MAX_LENGTH} حرف`);
            return;
        }

        setAboutUsText(editedText);
        dispatch(actions.updateAboutUs({ editedText, id }));
        toast.success("تم حفظ التغييرات بنجاح", {
            icon: "✅",
            position: "top-center"
        });
        setHasChanges(false);
    };

    const handleReset = () => {
        setEditedText(aboutUsText);
        setHasChanges(false);
        toast.info("تم التراجع عن التغييرات");
    };

    return (
        <Col xl={12} className="mb-4">
            <div className="d-flex align-items-center mb-3">
                <div className="avatar-sm me-3">
                    <span className="avatar-title rounded-circle bg-info-subtle text-info font-size-20">
                        <i className="bx bx-info-circle"></i>
                    </span>
                </div>
                <div>
                    <h5 className="mb-1">نبذة عنا</h5>
                    <p className="text-muted mb-0">إدارة وتحرير نص "عنا" الذي يظهر في التطبيق</p>
                </div>
            </div>

            <Card className="border shadow-sm" style={{ borderRadius: '12px' }}>
                <CardBody className="p-4">
                    <Nav tabs className="nav-tabs-custom nav-justified mb-4">
                        <NavItem>
                            <NavLink
                                style={{ cursor: "pointer", borderRadius: '8px 8px 0 0' }}
                                className={activeTab === '1' ? "active" : ""}
                                onClick={() => setActiveTab('1')}
                            >
                                <i className="bx bx-edit-alt me-2"></i>
                                تحرير
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                style={{ cursor: "pointer", borderRadius: '8px 8px 0 0' }}
                                className={activeTab === '2' ? "active" : ""}
                                onClick={() => setActiveTab('2')}
                            >
                                <i className="bx bx-show me-2"></i>
                                معاينة
                            </NavLink>
                        </NavItem>
                    </Nav>

                    <TabContent activeTab={activeTab}>
                        <TabPane tabId="1">
                            <Form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <Label for="aboutUsText" className="fw-semibold mb-0">
                                            <i className="bx bx-text me-2"></i>
                                            محتوى النص
                                        </Label>
                                        <div>
                                            <Badge 
                                                color={charRemaining < 100 ? "danger" : charRemaining < 200 ? "warning" : "success"}
                                                className="font-size-12"
                                            >
                                                {charRemaining} حرف متبقي
                                            </Badge>
                                            <Badge color="info" className="ms-2 font-size-12">
                                                {charCount} / {MAX_LENGTH}
                                            </Badge>
                                        </div>
                                    </div>
                                    
                                    <Input
                                        type="textarea"
                                        id="aboutUsText"
                                        value={editedText}
                                        onChange={(e) => setEditedText(e.target.value)}
                                        rows="10"
                                        maxLength={MAX_LENGTH}
                                        className="form-control-lg"
                                        style={{ 
                                            borderRadius: '8px',
                                            fontSize: '1rem',
                                            lineHeight: '1.6'
                                        }}
                                        placeholder="اكتب نص 'عنا' هنا..."
                                    />
                                    
                                    <small className="text-muted mt-2 d-block">
                                        <i className="bx bx-info-circle me-1"></i>
                                        اكتب نصاً واضحاً ومختصراً يعرّف بخدماتكم وقيمكم
                                    </small>
                                </div>

                                {hasChanges && (
                                    <Alert color="warning" className="border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                                        <i className="bx bx-error-circle me-2"></i>
                                        لديك تغييرات غير محفوظة
                                    </Alert>
                                )}

                                <div className="d-flex gap-2 mt-4">
                                    <Button 
                                        type="submit" 
                                        color="success" 
                                        size="lg"
                                        disabled={!hasChanges}
                                        style={{ borderRadius: '8px', minWidth: '120px' }}
                                    >
                                        <i className="bx bx-save me-2"></i>
                                        حفظ التغييرات
                                    </Button>
                                    
                                    {hasChanges && (
                                        <Button 
                                            type="button"
                                            color="secondary" 
                                            size="lg"
                                            onClick={handleReset}
                                            style={{ borderRadius: '8px' }}
                                        >
                                            <i className="bx bx-reset me-2"></i>
                                            تراجع
                                        </Button>
                                    )}
                                </div>
                            </Form>
                        </TabPane>

                        <TabPane tabId="2">
                            <Card className="border" style={{ borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
                                <CardBody className="p-4">
                                    <div className="d-flex align-items-center mb-3">
                                        <i className="bx bx-mobile font-size-24 text-primary me-2"></i>
                                        <h6 className="mb-0">معاينة كما تظهر في التطبيق</h6>
                                    </div>
                                    
                                    <div 
                                        className="preview-content p-4 bg-white rounded"
                                        style={{ 
                                            minHeight: '300px',
                                            fontSize: '1rem',
                                            lineHeight: '1.8',
                                            whiteSpace: 'pre-wrap',
                                            border: '1px solid #e3e3e3'
                                        }}
                                    >
                                        {editedText || (
                                            <div className="text-muted text-center py-5">
                                                <i className="bx bx-file-blank font-size-48 d-block mb-2"></i>
                                                لا يوجد محتوى للعرض
                                            </div>
                                        )}
                                    </div>
                                </CardBody>
                            </Card>
                        </TabPane>
                    </TabContent>
                </CardBody>
            </Card>

            <Alert color="info" className="mt-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                <i className="bx bx-info-circle me-2"></i>
                <strong>ملاحظة:</strong> سيظهر هذا النص في صفحة "عنا" في تطبيق العملاء وتطبيق مقدمي الخدمات.
            </Alert>
        </Col>
    );
}
